import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Icon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileModal } from '../../../../providers/MobileModalProvider';
import type { NavigationItem } from '../../../../tokens/navigationPaths';
import {
  OverflowDndArea,
  OverflowDragHandle,
  OverflowHeader,
  OverflowItemsList,
  OverflowModalWrapper,
  OverflowRow,
  OverflowRowButton,
} from './styles';

interface OverflowModalProps {
  items: NavigationItem[];
  onItemsChange: (items: NavigationItem[]) => void;
  title?: string;
}

export function OverflowModal({ items, onItemsChange, title }: OverflowModalProps) {
  const navigate = useNavigate();
  const { closeModal } = useMobileModal();
  const mixpanel = useMixpanel(MixpanelEventSource.MobileTabBar);
  const [orderedItems, setOrderedItems] = useState(items);

  useEffect(() => {
    setOrderedItems(items);
  }, [items]);

  const itemIds = useMemo(() => orderedItems.map(getNavItemKey), [orderedItems]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } })
  );

  const handlePressItem = useCallback(
    (item: NavigationItem) => {
      if (item.path == null) {
        return;
      }

      mixpanel.track(MixpanelEvent.NavigateTo, { [MixpanelEventProperty.Path]: item.path });
      if (item.mixpanelEvent) {
        mixpanel.track(item.mixpanelEvent);
      }
      navigate(item.path);
      closeModal();
    },
    [closeModal, mixpanel, navigate]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (over == null || active.id === over.id) {
        return;
      }

      setOrderedItems(prev => {
        const oldIndex = prev.findIndex(item => getNavItemKey(item) === active.id);
        const newIndex = prev.findIndex(item => getNavItemKey(item) === over.id);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          return prev;
        }

        mixpanel.track(MixpanelEvent.ReorderMobileTabBarItems, {
          [MixpanelEventProperty.FromIndex]: oldIndex,
          [MixpanelEventProperty.ToIndex]: newIndex,
        });

        const nextItems = arrayMove(prev, oldIndex, newIndex);
        onItemsChange(nextItems);
        return nextItems;
      });
    },
    [mixpanel, onItemsChange]
  );

  return (
    <OverflowModalWrapper>
      <OverflowHeader px="spacing16" py="spacing12">
        <Text textAlign="center" color="colorTextImportant" fontWeight="500">
          {title}
        </Text>
      </OverflowHeader>
      <OverflowDndArea>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <OverflowItemsList>
              {orderedItems.map(item => (
                <SortableOverflowItem key={getNavItemKey(item)} item={item} onPress={handlePressItem} />
              ))}
            </OverflowItemsList>
          </SortableContext>
        </DndContext>
      </OverflowDndArea>
    </OverflowModalWrapper>
  );
}

function SortableOverflowItem({ item, onPress }: { item: NavigationItem; onPress: (item: NavigationItem) => void }) {
  const id = getNavItemKey(item);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <OverflowRow ref={setNodeRef} style={style}>
      <OverflowRowButton type="button" onClick={() => onPress(item)}>
        <Icon thin size={20} icon={item.icon ?? IconName.Table} />
        <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" flex="1 1 0" color="colorTextImportant">
          {item.label}
        </Text>
      </OverflowRowButton>
      <OverflowDragHandle data-testid="mobile-tab-overflow-drag-handle" {...attributes} {...listeners}>
        <Icon size={20} icon={IconName.DragVertical} />
      </OverflowDragHandle>
    </OverflowRow>
  );
}

function getNavItemKey(item: NavigationItem) {
  return item.id ?? item.path ?? '';
}
